import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Keep list filters in the URL query string instead of component state.
 *
 * Admin work is collaborative and interrupt-driven. With filters held in
 * `useState` a support agent cannot paste "the queue I'm looking at" into a
 * message, a browser refresh silently drops back to page 1 of everything, and
 * the back button leaves the page instead of undoing a filter. Putting the
 * filters in the URL fixes all three and makes the Overview metric cards
 * linkable: "12 pending transfers" can navigate straight to that exact list.
 *
 * Values equal to their default are removed from the URL, so the common case
 * stays a clean `/admin/users` rather than a wall of empty parameters.
 *
 * @param defaults - parameter name to default value. Defines the whole schema:
 *   a key absent here is not read or written.
 */
export function useFilterParams(defaults) {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const resolved = {};
    for (const [key, fallback] of Object.entries(defaults)) {
      const raw = searchParams.get(key);
      if (raw === null || raw === "") {
        resolved[key] = fallback;
      } else if (typeof fallback === "number") {
        const parsed = Number(raw);
        // A hand-edited or truncated URL must not put the page into a broken
        // state - fall back rather than propagating NaN into a query.
        resolved[key] = Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
      } else {
        resolved[key] = raw;
      }
    }
    return resolved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, JSON.stringify(defaults)]);

  const setValues = useCallback(
    (changes, { replace = false } = {}) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (!(key in defaults)) continue;
            if (value === defaults[key] || value === "" || value == null) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        { replace }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams, JSON.stringify(defaults)]
  );

  /**
   * Apply filter changes and return to the first page.
   *
   * Almost every filter change should use this: narrowing a 400-row result to
   * 12 while sitting on page 5 otherwise shows a confusing empty table.
   */
  const setFilters = useCallback(
    (changes) => setValues({ ...changes, offset: 0 }),
    [setValues]
  );

  const isFiltered = useMemo(
    () =>
      Object.entries(defaults).some(
        ([key, fallback]) => key !== "offset" && values[key] !== fallback
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, JSON.stringify(defaults)]
  );

  const clearFilters = useCallback(() => {
    const cleared = {};
    for (const key of Object.keys(defaults)) cleared[key] = defaults[key];
    setValues(cleared);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValues, JSON.stringify(defaults)]);

  return { values, setValues, setFilters, clearFilters, isFiltered };
}
